// src/components/video/ParticipantTile.tsx
import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideoSlash,
  faMicrophoneSlash,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import NetworkIndicator from "./notificationElements/networkIndicator/NetworkIndicator";
import { Participant } from "../../../interfaces/video/video";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import AudioOrb from "../../ui/Video/AudioOrb";
import { MutedMicrophoneIcon } from "../SvgIcons/MutedMicrophoneIcon";
import { MicrophoneIcon } from "../SvgIcons/MicroPhoneIcon";

interface ParticipantTileProps {
  participant: Participant;
  isSharing?: boolean;
  size: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ParticipantTile: React.FC<ParticipantTileProps> = ({
  participant,
  isSharing,
  size
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  console.log(
    `Rendering Tile for: ${participant.displayName}, ` +
    `with userId: ${participant.userId}, ` +
    `isCameraOn: ${participant.isCameraOn}`
  );

  const getFormattedDipplayName = (displayName : string = "") => {
    const rawName = displayName ?? '';
    const hasYou = /\(you\)$/i.test(rawName.trim());
    const displayNameSafe = participant.isLocal && !hasYou ? `${rawName} (You)` : rawName;
    return {displayNameSafe , rawName};
  }
  
  useEffect(() => {
    const videoElement = videoRef.current;
    // Use a global audio pool so audio keeps playing even if the tile unmounts or is hidden.
    // Keyed by participant.feedId (fallback to userId/displayName if feedId not present).
    const audioPoolKey = participant.feedId ?? participant.userId ?? participant.displayName ?? '__unknown_participant';

    if (videoElement) {
      if (participant.stream) {
        videoElement.srcObject = participant.stream;
        videoElement.play().catch((error) => {
          console.error(
            `Error playing video stream for ${participant.displayName}:`,
            error
          );
        });
      } else {
        // Clear video when no stream
        try {
          videoElement.pause();
          // @ts-ignore
          videoElement.srcObject = null;
        } catch (e) {
          // ignore
        }
      }
    }

    // Only create audio elements for remote participants. Local audio is captured locally.
    if (!participant.isLocal) {
      // @ts-ignore attach a global map to the window to persist audio elements across mounts
      const pool = (window as any).__sproutAudioPool = (window as any).__sproutAudioPool || new Map<string, HTMLAudioElement>();

      let audioEl = pool.get(audioPoolKey);
      if (!audioEl && participant.stream) {
        audioEl = document.createElement('audio');
        audioEl.autoplay = true;
        audioEl.muted = false;
        audioEl.playsInline = true;
        audioEl.style.display = 'none';
        // Attach to DOM so browsers allow autoplay in some restrictive environments
        try {
          document.body.appendChild(audioEl);
        } catch (e) {
          // ignore if document.body not available (rare)
        }
        pool.set(audioPoolKey, audioEl);
      }

      if (audioEl) {
        if (participant.stream) {
          try {
            // @ts-ignore
            audioEl.srcObject = participant.stream;
            audioEl.play().catch((e: any) => console.error(`Audio element play error for ${participant.displayName}:`, e));
          } catch (e) {
            console.error('Error attaching stream to audio element', e);
          }
        } else {
          // If stream removed, pause and cleanup audio element from pool
          try {
            audioEl.pause();
            // @ts-ignore
            audioEl.srcObject = null;
            if (audioEl.parentElement) audioEl.parentElement.removeChild(audioEl);
          } catch (e) {
            // ignore
          }
          pool.delete(audioPoolKey);
        }
      }
    }

    // Cleanup: Don't remove persistent audio elements on unmount here; leave them to be
    // cleaned when their stream becomes null or the participant disconnects elsewhere.
    return () => {
      // If participant has no stream at cleanup time, ensure any pooled audio is removed.
      if (!participant.stream) {
        // @ts-ignore
        const pool = (window as any).__sproutAudioPool as Map<string, HTMLAudioElement> | undefined;
        if (pool) {
          const audioEl = pool.get(audioPoolKey);
          if (audioEl) {
            try {
              audioEl.pause();
              // @ts-ignore
              audioEl.srcObject = null;
              if (audioEl.parentElement) audioEl.parentElement.removeChild(audioEl);
            } catch (e) {
              // ignore
            }
            pool.delete(audioPoolKey);
          }
        }
      }
    };
  }, [participant.stream, participant.isLocal, participant.displayName]);

  const isLoading = !participant.isLocal && (!participant.stream || ['new', 'connecting'].includes(participant.connectionState ?? ''));
  const isFailed = !participant.isLocal && participant.connectionState === 'failed';
  const isSmallSize = (size === "sm" || size === "xs") ? true : false;


  return (
    <div
      className="relative overflow-hidden w-full h-full" // Changed rounded-3 to rounded-4 and ensure it fills grid cell
      style={{ 
        height: '100%',
        minHeight: 0 , 
        borderRadius: "1rem",
      }}
    >
      <video
        ref={videoRef}
        className="w-100 h-100"
        style={{
          objectFit: "cover",
          visibility: participant.isCameraOn && !isLoading && !isFailed ? "visible" : "hidden",
        }}
        autoPlay
        playsInline
        muted // Always mute the video element; audio is handled separately for remote participants.
      />
      
      {/* Avatar/AudioOrb background, shown when camera is off OR when loading/failed */}
      {(!participant.isCameraOn || isLoading || isFailed) && (
        <div className="w-full h-full flex items-center justify-center bg-[#E3E3E3] absolute top-0 left-0">
          <AudioOrb stream={participant.stream} />
          <div className="z-10">
             <AvatarImage size={size} userId={participant.userId ?? participant?.displayName ?? ""} />
          </div>
        </div>
      )}

      {/* Loading/Failed Overlay */}
      {(isLoading || isFailed) && (
        <div className="w-full h-full flex items-center justify-center absolute top-0 left-0 z-20" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          {isLoading && (
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
          {isFailed && (
            <span className="text-danger">Connection failed</span>
          )}
        </div>
      )}

      {participant.isLocal &&
        participant.networkQuality &&
        typeof participant.latency === "number" && (
          <NetworkIndicator
            quality={participant.networkQuality}
            latency={participant.latency}
          />
        )}

        <div 
          className={`bg-[#FFFFFFE5] rounded-lg flex items-center w-fit gap-2 absolute`} 
          style={{
            padding: (isSmallSize) ? "0.225rem 0.625rem" : "0.5rem 0.875rem",
            zIndex: 30,
            bottom: (isSmallSize) ? "0.625rem" : "1rem",
            left: (isSmallSize) ? "0.625rem" : "1rem"
          }}
        >
          {
            participant?.isMuted ? (
              <MutedMicrophoneIcon size={(isSmallSize) ? 14 : 18} color={"#dc2626"}/>
            ) : (
              <FontAwesomeIcon icon={faMicrophone} className={`${(isSmallSize) ? "text-sm" : "text-xl"}`} color="#AAADBF"/>
            )
          }

          <p className="m-0 text-black" title={getFormattedDipplayName(participant?.displayName)?.rawName || ""} 
            style={{
              fontSize : (isSmallSize) ? "0.875rem" : "1rem"
            }}
          >
            {
              getFormattedDipplayName(participant?.displayName)?.displayNameSafe || ""
            }
          </p>
        </div>
    </div>
  );
};
